import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Table, Badge, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import config from '../../config';

const ProductMovementModal = ({ show, onHide, productId, productName }) => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
    const fetchMovements = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
  
        const response = await axios.get(
          `${config.API_BASE_URL}/inventory/product/${productId}/movements`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
  
        if (response.data.success) {
          setMovements(response.data.data);
        } else {
          setError('Failed to fetch movement data');
        }
      } catch (err) {
        console.error('Error fetching movements:', err);
        setError(err.response?.data?.message || 'Failed to fetch movement data');
      } finally {
        setLoading(false);
      }
    }, [productId]);

  useEffect(() => {
    if (show && productId) {
      fetchMovements();
    }
  }, [show, productId, fetchMovements]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMovementTypeBadge = (movement) => {
    if (movement.isInward) {
      return <Badge bg="success">Inward</Badge>;
    } else if (movement.isOutward) {
      return <Badge bg="danger">Outward</Badge>;
    }
    return <Badge bg="secondary">Other</Badge>;
  };

  const getSourceBadge = (source) => {
    const sources = {
      'Purchase Order': 'primary',
      'Sales Order': 'info',
      'Production': 'warning',
      'Manual': 'secondary',
      'Internal Transfer': 'dark'
    };

    return (
      <Badge bg={sources[source] || 'secondary'}>
        {source}
      </Badge>
    );
  };

  const formatQuantity = (quantity, unit) => {
    if (!quantity) return '-';
    return `${quantity} ${unit || ''}`;
  };

  const formatPackingInfo = (movement) => {
    if (!movement.numberOfBags) return '-';

    const packing = movement.packingMaterial;
    if (packing && packing.name) {
      return `${movement.numberOfBags} bags of ${packing.name}`;
    }
    return `${movement.numberOfBags} bags`;
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <i className="fas fa-history me-2"></i>
          Product Movement History - {productName || 'Unknown Product'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading movement history...</p>
          </div>
        )}

        {error && (
          <Alert variant="danger" className="m-3">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <div className="mt-2">
              <Button variant="outline-danger" size="sm" onClick={fetchMovements}>
                <i className="fas fa-sync-alt me-1"></i>Try Again
              </Button>
            </div>
          </Alert>
        )}

        {!loading && !error && movements.length === 0 && (
          <Alert variant="info" className="m-3">
            <i className="fas fa-info-circle me-2"></i>
            No movement records found for this product.
          </Alert>
        )}

        {!loading && !error && movements.length > 0 && (
          <div className="table-responsive">
            <Table striped bordered hover className="mb-0">
              <thead className="table-dark">
                <tr>
                  <th className="text-center">Date</th>
                  <th className="text-center">Type</th>
                  <th className="text-center">Source</th>
                  <th className="text-center">Quantity</th>
                  <th className="text-center">Packing</th>
                  <th className="text-center">Location</th>
                  {/* <th className="text-center">Quality</th> */}
                  <th className="text-center">Batch/Ref</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((movement, index) => (
                  <tr key={movement._id || index}>
                    <td className="text-center">
                      <div>
                        <strong>{formatDate(movement.date).split(',')[0]}</strong>
                        <br />
                        <small className="text-muted">
                          {formatDate(movement.date).split(',')[1]}
                        </small>
                      </div>
                    </td>
                    <td className="text-center">
                      {getMovementTypeBadge(movement)}
                    </td>
                    <td className="text-center">
                      {getSourceBadge(movement.referenceType)}
                    </td>
                    <td className="text-center">
                      <strong className={movement.movementType === 'Inward' ? 'text-success' : 'text-danger'}>
                        {movement.movementType === 'Inward' ? '+' : '-'}
                        {formatQuantity(movement.quantity, movement.unit)}
                      </strong>
                    </td>
                    <td className="text-center">
                      {formatPackingInfo(movement)}
                    </td>
                    <td className="text-center">
                      <div>
                        <div>
                          <i className="fas fa-warehouse me-1"></i>
                          {movement.warehouseLocation?.label || 'Unknown'}
                        </div>
                        {movement.batchNumber && (
                          <small className="text-muted">
                            Batch: {movement.batchNumber}
                          </small>
                        )}
                      </div>
                    </td>
                    {/* <td className="text-center">
                      {movement.quality && (
                        <Badge bg={movement.quality === 'good' ? 'success' : 'warning'}>
                          {movement.quality.toUpperCase()}
                        </Badge>
                      )}
                    </td> */}
                    <td className="text-center">
                      {movement.source && (
                        <small className="text-muted">
                          {movement.source}
                        </small>
                      )}
                    </td>
                    <td>
                      {movement.notes && (
                        <small className="text-muted">
                          {movement.notes}
                        </small>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {!loading && !error && movements.length > 0 && (
          <div className="p-3 bg-light border-top">
            <Row>
              <Col md={6}>
                <strong>Total Records:</strong> {movements.length}
              </Col>
              <Col md={6} className="text-end">
                <strong>Inward:</strong> {movements.filter(m => m.isInward).length} |
                <strong> Outward:</strong> {movements.filter(m => m.isOutward).length}
              </Col>
            </Row>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          <i className="fas fa-times me-2"></i>Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProductMovementModal;
